import { firstValueFrom } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ListeParcs, Parc, ReponseApi, TicketBrut } from './EntiteProjet';
import { Authentification } from './authentification';
import { environment } from '../../environment/environment';
import { extraireReponse } from './api';
import { ErreurApi } from './api-error';
import { PaginationMeta } from './pagination';
import { appliquerOptionsTickets, OptionsTickets } from './tickets-query';
import { ModeAffichageGraphique } from './installation';
import { ParcInformations } from './parc-informations.types';
import { FiltresInterventions, ParcInterventionsResponse } from './interventions';

/** Certains parcs existants n’ont pas de libellé dans la réponse du serveur. */
type ParcApi = Omit<Parc, 'libelle'> & { libelle: string | null };

/** Réponse de `GET /parc/:id/tickets` */
export type ParcTicketsResponse = {
  tickets: TicketBrut[];
  pagination?: PaginationMeta;
};

/** Un point de données retourné par `GET /parc/:id/indicateurs`. */
export type ParcChartPoint = {
  date: number;
  production: number;
  ensoleillement: number;
};

/** Détail par installation retourné par `GET /parc/:id/chiffres-utiles` en mode debug. */
export type ChiffresUtilesDetailInstallation = {
  id_installation: number;
  total_production: number;
  total_revenus: number;
  last_value_ref_puissance: number | null;
};

/** Un point de données retourné par `GET /parc/:id/chiffres-utiles`. */
export type ReponseChiffresUtiles = {
  co2_evite: number;
  energie_cumulee: number;
  energie_journaliere: number;
  energie_veille: number;
  puissance: number;
  revenus_cumules: number;
  detail?: ChiffresUtilesDetailInstallation[];
};
/** Libellé et unité d'une grandeur, issus des métadonnées de la réponse indicateurs. */
export type ParcChampInfo = {
  libelle: string;
  unite?: string;
};

/** Labels traduits issus des métadonnées de la réponse indicateurs. */
export type ParcChartLabels = {
  production: ParcChampInfo;
  ensoleillement: ParcChampInfo;
};

export type SourceEntry = { date: string; [id: string]: string };

export type ReferentsParcReponse = {
  metadatas?: { format?: string; date_debut?: string; date_fin?: string; nb_points?: number };
  champs?: { val?: { libelle?: string; unite?: string | null } };
  datas?: Array<{ date: string; val: number }>;
  sources?: Record<string, SourceEntry[]>;
};

/** Résultat structuré de {@link ParcService.getParcIndicateurs}. */
export type ParcIndicateursDonnees = {
  data: ParcChartPoint[];
  labels: ParcChartLabels;
};

@Injectable({
  providedIn: 'root',
})
export class ParcService {
  constructor(
    private auth: Authentification,
    private http: HttpClient,
  ) {}

  /** Contrat de création : POST /parc, à confirmer avec le backend. */
  async creerParc(demande: { libelle: string; installation_ids: number[] }): Promise<Parc> {
    const res = await firstValueFrom(
      this.http.post<ReponseApi<Parc>>(
        `${environment.apiUrl}/parc`,
        {
          libelle: demande.libelle.trim(),
          installation_ids: [...new Set(demande.installation_ids)],
        },
        this.auth.prepareHeaders(),
      ),
    );
    const parc = extraireReponse(res);
    if (!parc) throw new ErreurApi('Le serveur n’a pas retourné le parc créé.', 'reponse_invalide');
    return parc;
  }

  /**
   * Récupère la liste des parcs depuis l'API.
   * Les erreurs HTTP et métier sont propagées au composant appelant.
   */
  async listeParcs(): Promise<ListeParcs | null> {
    const url = `${environment.apiUrl}/parc`;
    const res = await firstValueFrom(
      this.http.get<ReponseApi<ParcApi[]>>(url, this.auth.prepareHeaders()),
    );
    // Le reste de l’application peut trier et afficher chaque parc sans tester null.
    return (
      extraireReponse(res)?.map((parc) => ({
        ...parc,
        libelle: parc.libelle?.trim() || `Sans nom (n° ${parc.id})`,
      })) ?? null
    );
  }

  /** Récupère les informations générales et les contacts associés à un parc. */
  async getParc(parcId: number): Promise<ParcInformations | null> {
    const url = `${environment.apiUrl}/parc/${parcId}`;
    const res = await firstValueFrom(
      this.http.get<ReponseApi<ParcInformations> | ParcInformations>(
        url,
        this.auth.prepareHeaders(),
      ),
    );

    if ('statut' in res) return extraireReponse(res);
    return res;
  }

  async getParcTickets(
    parcId: number,
    etat?: string,
    page?: number,
    limit?: number,
    search?: string,
    optionsTickets?: OptionsTickets,
  ): Promise<ParcTicketsResponse | null> {
    const url = `${environment.apiUrl}/parc/${parcId}/tickets`;
    let params = appliquerOptionsTickets(new HttpParams(), optionsTickets);
    if (etat) {
      params = params.set('etat', etat);
    }
    if (page != null) {
      params = params.set('page', page);
    }
    if (limit != null) {
      params = params.set('limit', limit);
    }
    if (search) {
      params = params.set('search', search);
    }
    const options = {
      ...this.auth.prepareHeaders(),
      params,
    };

    const res = await firstValueFrom(
      this.http.get<ReponseApi<ParcTicketsResponse | unknown[]>>(url, options),
    );
    const reponse = extraireReponse(res);
    if (reponse === null) return null;
    if (Array.isArray(reponse)) return { tickets: reponse as TicketBrut[] };
    const payload = reponse as ParcTicketsResponse;
    return {
      tickets: Array.isArray(payload.tickets) ? payload.tickets : [],
      pagination: payload.pagination,
    };
  }

  async getParcInterventions(
    parcId: number,
    filtres: FiltresInterventions = {},
  ): Promise<ParcInterventionsResponse | null> {
    const url = `${environment.apiUrl}/parc/${parcId}/interventions`;
    let params = new HttpParams();
    if (filtres.debut) params = params.set('debut', filtres.debut);
    if (filtres.fin) params = params.set('fin', filtres.fin);

    const res = await firstValueFrom(
      this.http.get<ReponseApi<ParcInterventionsResponse>>(url, {
        ...this.auth.prepareHeaders(),
        params,
      }),
    );
    return extraireReponse(res);
  }

  /**
   * Récupère les données de production d'un parc pour la période correspondant au mode.
   *
   * URL : `GET /parc/:id/indicateurs?type=production&format=<jour|mois>&debut=...&fin=...`
   *
   * @param parcId  Identifiant du parc.
   * @param mode    Granularité d'affichage : `'jour'` | `'mois'` | `'annee'`.
   * @param fin     Date de fin de la période (défaut : aujourd'hui).
   */
  async getParcIndicateurs(
    parcId: number,
    mode: ModeAffichageGraphique = 'jour',
    fin?: Date,
  ): Promise<ParcIndicateursDonnees> {
    const { debutApi, finApi, format } = this.calculerPlage(mode, fin);
    const url = `${environment.apiUrl}/parc/${parcId}/indicateurs`;
    const params = new HttpParams()
      .set('type', 'production')
      .set('format', format)
      .set('debut', debutApi)
      .set('fin', finApi);

    type LigneData = { date: string; production?: number; ensoleillement?: number };
    type ReponseIndicateurs = {
      metadatas?: unknown;
      champs?: {
        production?: { libelle?: string; unite?: string };
        ensoleillement?: { libelle?: string; unite?: string };
      };
      datas?: LigneData[];
    };

    const res = await firstValueFrom(
      this.http.get<ReponseApi<ReponseIndicateurs>>(url, { ...this.auth.prepareHeaders(), params }),
    );
    const reponse = extraireReponse(res);
    const vide: ParcIndicateursDonnees = {
      data: [],
      labels: {
        production: { libelle: 'Production' },
        ensoleillement: { libelle: 'Ensoleillement' },
      },
    };
    if (!reponse) return vide;

    const labels: ParcChartLabels = {
      production: {
        libelle: reponse.champs?.production?.libelle ?? 'Production',
        unite: reponse.champs?.production?.unite,
      },
      ensoleillement: {
        libelle: reponse.champs?.ensoleillement?.libelle ?? 'Ensoleillement',
        unite: reponse.champs?.ensoleillement?.unite,
      },
    };

    const data: ParcChartPoint[] = (reponse.datas ?? [])
      .map((ligne) => ({
        date: Date.parse(ligne.date),
        production: ligne.production ?? 0,
        ensoleillement: ligne.ensoleillement ?? 0,
      }))
      .filter((p) => Number.isFinite(p.date));

    return { data, labels };
  }

  /**
   * Calcule les bornes de la période et le format API à partir du mode d'affichage.
   * - `'jour'`  → plage ±1 jour, format `'minute'`
   * - `'mois'`  → 1er au dernier jour du mois, format `'jour'`
   * - `'annee'` → 1er jan au 31 déc, format `'mois'`
   */
  private calculerPlage(mode: ModeAffichageGraphique, fin?: Date) {
    const ref = fin ? new Date(fin) : new Date();
    const fmt = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    switch (mode) {
      case 'mois': {
        const debut = new Date(ref.getFullYear(), ref.getMonth(), 1);
        const finDate = new Date(ref.getFullYear(), ref.getMonth() + 1, 0);
        return { debutApi: fmt(debut), finApi: fmt(finDate), format: 'jour' };
      }
      case 'annee': {
        const annee = ref.getFullYear();
        return { debutApi: `${annee}-01-01`, finApi: `${annee}-12-31`, format: 'mois' };
      }
      default: {
        const debut = new Date(ref);
        debut.setDate(ref.getDate() - 1);
        const finDate = new Date(ref);
        finDate.setDate(ref.getDate() + 1);
        return { debutApi: fmt(debut), finApi: fmt(finDate), format: 'minute' };
      }
    }
  }

  async getReferentsParc(
    parcId: number,
    mode: ModeAffichageGraphique,
    dateFin: Date,
    typeReferent: 'ref_production' | 'ref_ensoleillement',
  ): Promise<ReferentsParcReponse | null> {
    const fmt = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const format = mode === 'annee' ? 'mois' : mode === 'mois' ? 'jour' : 'minute';
    const url = `${environment.apiUrl}/parc/${parcId}/indicateurs`;
    const params = new HttpParams()
      .set('type', 'referent')
      .set('format', format)
      .set('debut', fmt(dateFin))
      .set('options[details]', '1')
      .set('options[referent]', typeReferent);

    const res = await firstValueFrom(
      this.http.get<ReponseApi<ReferentsParcReponse>>(url, {
        ...this.auth.prepareHeaders(),
        params,
      }),
    );
    return extraireReponse(res);
  }

  public async getChiffresUtiles(parcId: number, debug = false) {
    const url = `${environment.apiUrl}/parc/${parcId}/chiffres-utiles`;
    const params = debug ? new HttpParams().set('debug', 'true') : undefined;
    const res = await firstValueFrom(
      this.http.get<ReponseApi<ReponseChiffresUtiles>>(url, {
        ...this.auth.prepareHeaders(),
        params,
      }),
    );
    return extraireReponse(res);
  }
}
